import DataTableColumnHeader from '@/components/datatable/DataTableColumnHeader.vue';
import DataTableCurrency from '@/components/datatable/DataTableCurrency.vue';
import DataTableTdCheckbox from '@/components/datatable/DataTableTdCheckbox.vue';
import DataTableThCheckbox from '@/components/datatable/DataTableThCheckbox.vue';
import VoucherTableActions from '@/components/vouchers/VoucherTableActions.vue';
import VoucherTableAssignedTo from '@/components/vouchers/VoucherTableAssignedTo.vue';
import type { Voucher, EconomicDimension } from '@/types';
import { formatDate } from '@/utils/helpers';
import type { ColumnDef } from '@tanstack/vue-table';
import { h } from 'vue';

export function createVoucherColumns(dimensions: EconomicDimension[]): ColumnDef<Voucher>[] {
    const enabledDimensions = dimensions.filter(d => d.is_active && d.is_enabled);
    
    return [
    {
        accessorKey: 'select',
        header: ({ table }) => h(DataTableThCheckbox, { table }),
        cell: ({ row }) => h(DataTableTdCheckbox, { row }),
        enableHiding: false,
        size: 24,
        enableColumnFilter: false,
    },
    {
        accessorKey: 'Date',
        header: ({ column }) =>
            h(DataTableColumnHeader, {
                column: column,
                title: 'vouchers.voucher_table.date',
            }),
        cell: ({ row }) => {
            return formatDate(row.original.Date);
        },
        filterFn: 'dateRange',
        meta: {
            filterType: 'dateRange',
        },
    },
    {
        accessorKey: 'VoucherNumber',
        header: ({ column }) =>
            h(DataTableColumnHeader, {
                column: column,
                title: 'vouchers.voucher_table.voucher',
            }),
        meta: {
            filterType: 'number',
        },
    },
    {
        accessorKey: 'Text',
        header: ({ column }) =>
            h(DataTableColumnHeader, {
                column: column,
                title: 'vouchers.voucher_table.text',
            }),
    },
    {
        accessorKey: 'Amount',
        header: ({ column }) =>
            h(DataTableColumnHeader, {
                column: column,
                title: 'vouchers.voucher_table.amount',
            }),
        cell: ({ row }) => {
            return h(DataTableCurrency, {
                value: row.original.Amount,
            });
        },
        meta: {
            filterType: 'currency',
        },
    },
    {
        accessorKey: 'Account',
        header: ({ column }) =>
            h(DataTableColumnHeader, {
                column: column,
                title: 'vouchers.voucher_table.account',
            }),
    },
    {
        accessorKey: 'ContraAccount',
        header: ({ column }) =>
            h(DataTableColumnHeader, {
                column: column,
                title: 'vouchers.voucher_table.contra_account',
            }),
    },
    {
        accessorKey: 'assigned_names',
        header: ({ column }) =>
            h(DataTableColumnHeader, {
                column: column,
                title: 'vouchers.voucher_table.assigned_to',
            }),
        cell: ({ row }) => {
            return h(VoucherTableAssignedTo, {
                voucher: row.original,
            });
        },
    },
    {
        accessorKey: 'project_name',
        header: ({ column }) =>
            h(DataTableColumnHeader, {
                column: column,
                title: 'vouchers.voucher_table.project',
            }),
        filterFn: 'arrIncludesSome',
        meta: {
            filterType: 'checkbox',
        },
    },
    {
        accessorKey: 'card_number',
        header: ({ column }) =>
            h(DataTableColumnHeader, {
                column: column,
                title: 'vouchers.voucher_table.card',
            }),
    },
    // Dynamic dimension columns
    ...enabledDimensions.map((dimension): ColumnDef<Voucher> => ({
        accessorKey: `dimension_${dimension.id}`,
        header: ({ column }) =>
            h(DataTableColumnHeader, {
                column: column,
                title: dimension.name,
            }),
        filterFn: 'arrIncludesSome',
        meta: {
            filterType: 'checkbox',
        },
    })),
    {
        accessorKey: 'comment',
        header: ({ column }) =>
            h(DataTableColumnHeader, {
                column: column,
                title: 'vouchers.voucher_table.comment',
            }),
    },
    {
        id: 'actions',
        enableHiding: false,
        header: ({ column }) =>
            h(DataTableColumnHeader, {
                column: column,
                title: 'tables.actions',
            }),
        cell: ({ row }) => {
            return h(VoucherTableActions, {
                voucher: row.original,
            });
        },
    },
    ];
}

export const columns = createVoucherColumns([]);
